import {gql} from '@apollo/client';
import {css, StyleSheet} from 'aphrodite';
import reactUpdate from 'immutability-helper';
import React from 'react';
import isEqual from 'react-fast-compare';
import {connect} from 'react-redux';
import _ from 'underscore';

import BenchlingConfig from 'BenchlingConfig';
import {Tag} from 'folder-items/FileConstants';
import {ErrorHandlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {graphQLArrayToREST} from 'graphql-client/declarative-translations/external/translate';
import {
  FromServerRunOutputSamplesTable,
  RunOutputSamplesTable,
} from 'lab-automation/output-samples/RunOutputSamplesTableConstants';
import {DataTable} from 'notebook/data-tables/DataTableConstants';
import {FromServerResultsTable, ResultsTable} from 'notebook/integrations/results/ResultsTableConstants';
import {CidIdMap} from 'notebook/notes/NoteConstants';
import {NoteEditor, NoteEditorProps} from 'notebook/notes/NoteEditorTypes';
import {ReviewSummary} from 'notebook/reviews/ReviewConstants';
import * as ReviewUtil from 'notebook/reviews/ReviewUtil';
import {ProxiedNotePropTypes} from 'notebook/util/NoteUtilConstants';
import notifications from 'notifications';
import {BenchlingState} from 'redux-stores/reduceApp';
import {BenchlingDispatch} from 'redux-stores/Store';
import {AssayRunSchema, AssaySchema} from 'results/assay-schemas/AssaySchemaConstants';
import * as AssayRecordActions from 'results/assays/AssayRecordActions';
import {AssayResult, AssayRun, DATA_MISSING, isOversized} from 'results/assays/AssayRecordConstants';
import * as AssayRecordDAO from 'results/assays/AssayRecordDAO';
import {DataMissing} from 'results/reduceResults';
import {MODEL_NAMES} from 'results/ResultsNamingConstants';
import {prepareNLADStructuredTablesForStore} from 'runs/NoteLinkedAssayDataUtil';
import withMaybeRunFetching from 'runs/withMaybeRunFetching';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import memoizeLastCall from 'util/memoizeLastCall';

import {AssaySchemasQuery} from './__generated/AssaySchemasQuery';
import areAllArchivableOutputFilesArchived from './areAllArchivableOutputFilesArchived';
import {
  DisplayConfig,
  NLADVersionedAutomationFiles,
  NLADVersionedData,
  NoteLinkedAssayData,
} from './AttachmentConstants';
import EmptyAssayDataMessage from './EmptyAssayDataMessage';
import NoteLinkedAssayGroup from './NoteLinkedAssayGroup';

interface NoteLinkedAssayDataExternalProps extends ProxiedNotePropTypes {
  assayResultSchemas: Array<AssaySchema>;
  assayRunSchemaInfo: {
    assayRunSchema: AssayRunSchema;
    versionedName: string | null;
    versionedFields: Array<SchemaField> | null;
  };
  assayRunInfo: {
    assayRunId: string | null;
    versionedTags: Array<Tag> | null;
    versionedConnectionId: string | null;
    versionedEquipmentId: string | null;
  };
  automationFiles: NLADVersionedAutomationFiles;
  displayConfigBySchemaId: {[schemaId: string]: DisplayConfig};
  dataTables: Array<DataTable>;
  provenanceUuid: string;
  editor: NoteEditor;
  editorProps: NoteEditorProps;
  versionedStructuredTables: Array<RunOutputSamplesTable | ResultsTable>;
  usedTableNames: () => Array<string>;
}

/**
 * Return DATA_MISSING if any of the results are DATA_MISSING, otherwise a list of assay results
 */
function _extractAssayResults(
  assayResultsById: {[assayResultId: string]: AssayResult | DataMissing},
  ids: Array<string>
): Array<AssayResult> | string {
  // eslint-disable-next-line no-restricted-syntax
  const results = _.values(_.pick(assayResultsById, ids));
  if (results.some((result) => result === DATA_MISSING)) {
    return DATA_MISSING;
  } else {
    return results;
  }
}

/**
 * We need to memoize this function because _extractAssayResults is called by mapStateToProps and its
 * output is used to update the component's props. If the old props do not deep equal the new props, then
 * the component re-renders. Since _extractAssayResults creates a new object in each call, even if the values
 * are identical the component would unnecessarily rerender. Wrapping this in memoizeLastCall solves this by
 * returning the last value it computed if the arguements passed in are the same.
 */
const _memoizeExtractAssayResults = memoizeLastCall(
  (assayResultsById: {[assayResultId: string]: AssayResult | DataMissing}, ids: Array<string>) =>
    _extractAssayResults(assayResultsById, ids)
);

function areAssayResultsMissing(assayResults: Array<AssayResult> | string | null): assayResults is string {
  return assayResults === DATA_MISSING;
}

export function mapStateToProps(
  state: BenchlingState,
  ownProps: BaseNoteLinkedAssayDataFrameProps
): {
  assayResults: Array<AssayResult> | null;
  assayRun: AssayRun | null;
  childrenFetching: boolean;
  dataMissing: boolean;
  resultSchemaToCount: {[resultSchema: string]: number};
  review: ReviewSummary | null;
} {
  const {assayRunsById, assayRunsWithResultsFetching, assayResultsById, assayResultIdsByRunId} =
    state.models.results;
  const assayRun = assayRunsById[ownProps.assayRunInfo.assayRunId!] || null;
  const childrenFetching = assayRunsWithResultsFetching[ownProps.assayRunInfo.assayRunId!] || false;

  const assayResultIds = assayResultIdsByRunId[ownProps.assayRunInfo.assayRunId!];

  let assayResults = null;
  if (assayResultIds === DATA_MISSING) {
    assayResults = DATA_MISSING;
  } else if (assayResultIds) {
    assayResults = _memoizeExtractAssayResults(assayResultsById, assayResultIds);
  } else {
    assayResults = null;
  }

  const review = ReviewUtil.getReviewFromStore(ownProps.fileId);
  const resultSchemaToCount =
    (ownProps.assayRunInfo.assayRunId &&
      state.models.results.runIdToResultSchemaToCount[ownProps.assayRunInfo.assayRunId]) ||
    {};

  if (assayRun === DATA_MISSING || areAssayResultsMissing(assayResults)) {
    return {
      assayResults: null,
      assayRun: null,
      childrenFetching: false,
      dataMissing: true,
      resultSchemaToCount,
      review,
    };
  }

  return {
    assayResults,
    assayRun,
    childrenFetching,
    dataMissing: false,
    resultSchemaToCount,
    review,
  };
}

interface DispatchProps {
  onFetchAssayRun: () => void;
  onFetchAssayResultsForRun: () => void;
}

function mapDispatchToProps(
  dispatch: BenchlingDispatch,
  ownProps: BaseNoteLinkedAssayDataFrameProps
): DispatchProps {
  return {
    onFetchAssayResultsForRun(): void {
      if (ownProps.assayRunInfo.assayRunId) {
        dispatch(AssayRecordActions.fetchAssayResultsByRun(ownProps.assayRunInfo.assayRunId));
      }
    },
    onFetchAssayRun(): void {
      if (ownProps.assayRunInfo.assayRunId) {
        dispatch(AssayRecordActions.fetchAssayRuns([ownProps.assayRunInfo.assayRunId]));
      }
    },
  };
}

interface ConnectedProps {
  assayResults: Array<AssayResult> | null;
  assayRun: AssayRun | null;
  childrenFetching: boolean;
  dataMissing: boolean;
  resultSchemaToCount: {[resultSchema: string]: number};
  review: ReviewSummary | null;
}

interface BaseNoteLinkedAssayDataFrameProps extends NoteLinkedAssayDataExternalProps {
  registerObjects: (cidIdMap: CidIdMap) => void;
  storeState: () => void;
  updateHandler: (index: number, content: Partial<NoteLinkedAssayData>) => void;
  updateVersionedData: (updates: Partial<NLADVersionedData>) => NLADVersionedData;
  // allAssayResultSchemas are all accessible result schemas, while assayResultSchemas are just the result
  // schemas used in this NLAD
  allAssayResultSchemas?: Array<AssaySchema> | null;
}

export type NoteLinkedAssayDataFrameProps = BaseNoteLinkedAssayDataFrameProps &
  ConnectedProps &
  DispatchProps;

interface State {
  isRefreshing: boolean;
  refreshCounter: number;
}

export const ASSAY_SCHEMAS_QUERY = gql`
  query AssaySchemasQuery @skipAudit @engComponent(name: LAB_AUTOMATION) {
    ...AllAssaySchemasGraphQL
  }
  ${getFragmentWrapper('AllAssaySchemas').fragment}
`;

export class NoteLinkedAssayDataFrame extends React.Component<NoteLinkedAssayDataFrameProps, State> {
  override state: State = {
    isRefreshing: false,
    refreshCounter: 0,
  };
  override componentDidMount(): void {
    if (this.props.assayRunInfo.assayRunId != null && !BenchlingConfig.ELN_ENABLE_LEGACY_RUNS_USING_GRAPHQL) {
      this.props.onFetchAssayRun();
      this.props.onFetchAssayResultsForRun();
    }

    this._updateIfMissingAssaySchemas();
  }

  override componentDidUpdate(prevProps: NoteLinkedAssayDataFrameProps): void {
    if (!isEqual(prevProps, this.props)) {
      this._updateIfMissingAssaySchemas();
    }
    if (
      this.props.assayRunInfo.assayRunId != null &&
      this.props.assayRunInfo.assayRunId !== prevProps.assayRunInfo.assayRunId &&
      !BenchlingConfig.ELN_ENABLE_LEGACY_RUNS_USING_GRAPHQL
    ) {
      this.props.onFetchAssayRun();
      this.props.onFetchAssayResultsForRun();
    }
  }

  _updateIfMissingAssaySchemas(): void {
    /**
     * Compute any missing result schemas, and if present, update the component's persisted state.
     *
     * (Schemas may be missing if the user attaches results of a new schema to this run.)
     */
    if (!this.props.allAssayResultSchemas || !this.props.assayResults || !this.props.resultSchemaToCount) {
      return;
    }
    if (this.props.locked) {
      return;
    }

    const allAssayResultSchemasById = _.indexBy(this.props.allAssayResultSchemas, 'id');
    // eslint-disable-next-line no-restricted-syntax
    const schemaIdsWithResults = _.uniq(this.props.assayResults.map((result) => result.schemaId));

    const missingSchemas = _.difference(
      Object.keys(this.props.resultSchemaToCount),
      // eslint-disable-next-line no-restricted-syntax
      _.pluck(this.props.assayResultSchemas, 'id')
    )
      .filter(
        (schemaId) =>
          // If we update to include a schema before we have results for it, we'll render a table with a null
          // row until the data returns from the server - avoid that bad experience
          schemaIdsWithResults.includes(schemaId) ||
          // That doesn't apply, however, to oversized results, which we don't fetch in the first place
          isOversized(this.props.resultSchemaToCount[schemaId])
      )
      .map((schemaId) => allAssayResultSchemasById[schemaId]);

    if (missingSchemas.length) {
      this.props.updateHandler(this.props.index, {
        assayResultSchemas: this.props.assayResultSchemas.concat(missingSchemas),
      });
    }
  }

  _handleSetAssayRun = async (
    selectedRunId: string,
    assayResultSchemas: Array<AssaySchema>,
    runSchemaId: string,
    runDisplayConfig: DisplayConfig
  ): Promise<void> => {
    const versionedData = await AssayRecordDAO.createVersionedDataForRun(selectedRunId, this.props.fileId);
    // The _updateStructuredTablesHelper registers the new tables to have a cid.
    this._updateStructuredTablesHelper(versionedData.structuredTables, []);
    this.props.updateHandler(this.props.index, {
      assayRunId: selectedRunId,
      assayResultSchemas,
      displayConfigBySchemaId: reactUpdate(this.props.displayConfigBySchemaId, {
        [runSchemaId]: {$set: runDisplayConfig},
      }),
      versionedData: versionedData as unknown as NLADVersionedData,
    });
  };

  _handleSetProvenanceUuid = (provenanceUuid: string): void => {
    this.props.updateHandler(this.props.index, {
      provenanceUuid,
    });
  };

  _updateStructuredTablesHelper(
    structuredTables: Array<FromServerRunOutputSamplesTable | FromServerResultsTable>,
    existingTableNames: Array<string>
  ): void {
    for (const table of structuredTables) {
      table.fileId = this.props.fileId;
    }
    const usedTableNames = this.props.usedTableNames().filter((name) => !existingTableNames.includes(name));
    const cidIdMap = prepareNLADStructuredTablesForStore(structuredTables, usedTableNames);
    this.props.registerObjects(cidIdMap);
  }

  _refreshStructuredTablesHelper(
    structuredTables: Array<FromServerRunOutputSamplesTable | FromServerResultsTable>
  ): void {
    const existingTableNames = this.props.versionedStructuredTables.map((table) => table.name);
    this._updateStructuredTablesHelper(structuredTables, existingTableNames);
  }

  _handleRefreshVersionedData = async (): Promise<void> => {
    this.setState({isRefreshing: true});
    const versionedData = await AssayRecordDAO.createVersionedDataForRun(
      this.props.assayRunInfo.assayRunId!,
      this.props.fileId
    );
    this._refreshStructuredTablesHelper(versionedData.structuredTables);
    this.props.updateHandler(this.props.index, {
      versionedData: versionedData as unknown as NLADVersionedData,
    });
    this.setState((state) => ({isRefreshing: false, refreshCounter: state.refreshCounter + 1}));
  };

  _handleRemoveAssayRun = (): void => {
    if (!areAllArchivableOutputFilesArchived(this.props.automationFiles.automationOutputFiles ?? [])) {
      notifications.warn('Please archive all Results before deleting the Run');
    } else {
      this.props.updateHandler(this.props.index, {
        assayRunId: null,
        assayResultSchemas: [],
        versionedData: this.props.updateVersionedData({tags: null, structuredTables: []}),
      });
    }
  };

  _handleUpdateTags = (tags: Array<Tag>, connectionId: string | null): void => {
    this.props.updateHandler(this.props.index, {
      versionedData: this.props.updateVersionedData({tags, connectionId}),
    });
  };

  _handleUpdateAutomationFiles = (
    files: Partial<NLADVersionedAutomationFiles>,
    structuredTables?: Array<FromServerRunOutputSamplesTable | FromServerResultsTable>
  ): void => {
    const newVersionedData: Partial<NLADVersionedData> = {...files};
    if (structuredTables) {
      this._updateStructuredTablesHelper(structuredTables, []);
      newVersionedData.structuredTables = [
        ...this.props.versionedStructuredTables,
        ...(structuredTables as unknown as Array<RunOutputSamplesTable | ResultsTable>),
      ];
    }
    this.props.updateHandler(this.props.index, {
      versionedData: this.props.updateVersionedData(newVersionedData),
    });
  };

  _handleUpdateNoteLinkedAssayData = (partialNoteLinkedAssayData: Partial<NoteLinkedAssayData>): void => {
    this.props.updateHandler(this.props.index, partialNoteLinkedAssayData);
  };

  override render(): JSX.Element {
    if (this.props.dataMissing) {
      return (
        <EmptyAssayDataMessage indentation={this.props.indentation} title={`Attached ${MODEL_NAMES.Run}`}>
          <span>
            You do not have access to the attached assay data. The data may have been uploaded to a project
            you do not have read access to.
          </span>
        </EmptyAssayDataMessage>
      );
    }

    return (
      <div
        {...getTestProps({
          component: 'NoteLinkedAssayDataFrame',
          element: 'root',
        })}
        className={css(styles.noteLinkedAssayDataFrame)}
      >
        <NoteLinkedAssayGroup
          assayRunSchemaInfo={this.props.assayRunSchemaInfo}
          assayRunInfo={this.props.assayRunInfo}
          displayConfigBySchemaId={this.props.displayConfigBySchemaId}
          dataTables={this.props.dataTables}
          assayResults={this.props.assayResults}
          childrenFetching={this.props.childrenFetching}
          resultSchemaToCount={this.props.resultSchemaToCount}
          assayResultSchemas={this.props.assayResultSchemas}
          assayRuns={this.props.assayRun ? [this.props.assayRun] : null}
          automationFiles={this.props.automationFiles}
          provenanceUuid={this.props.provenanceUuid}
          review={this.props.review}
          handleRefreshVersionedData={this._handleRefreshVersionedData}
          handleRemoveAssayRun={this._handleRemoveAssayRun}
          handleSetAssayRun={this._handleSetAssayRun}
          handleSetProvenanceUuid={this._handleSetProvenanceUuid}
          handleUpdateNoteLinkedAssayData={this._handleUpdateNoteLinkedAssayData}
          handleUpdateTags={this._handleUpdateTags}
          handleUpdateAutomationFiles={this._handleUpdateAutomationFiles}
          cid={this.props.cid}
          fileId={this.props.fileId}
          indentation={this.props.indentation}
          index={this.props.index}
          locked={this.props.locked}
          viewer={this.props.viewer}
          versionedStructuredTables={this.props.versionedStructuredTables}
          usedTableNames={this.props.usedTableNames}
          editor={this.props.editor}
          editorProps={this.props.editorProps}
          refreshState={{isRefreshing: this.state.isRefreshing, refreshCounter: this.state.refreshCounter}}
        />
      </div>
    );
  }
}

const QueryingNoteLinkedAssayDataFrame = withMaybeRunFetching(NoteLinkedAssayDataFrame);

// eslint-disable-next-line react/prefer-stateless-function
export class WrappedNoteLinkedAssayDataFrame extends React.Component<NoteLinkedAssayDataFrameProps> {
  override render(): JSX.Element {
    const Component = BenchlingConfig.ELN_ENABLE_LEGACY_RUNS_USING_GRAPHQL
      ? QueryingNoteLinkedAssayDataFrame
      : NoteLinkedAssayDataFrame;
    return (
      <ErrorHandlingQuery<AssaySchemasQuery> query={ASSAY_SCHEMAS_QUERY}>
        {({data: assaySchemaData, loading: assaySchemaLoading}) => (
          <Component
            {...this.props}
            allAssayResultSchemas={
              assaySchemaLoading ? null : graphQLArrayToREST('AssaySchema', assaySchemaData!.resultSchemas)
            }
          />
        )}
      </ErrorHandlingQuery>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(WrappedNoteLinkedAssayDataFrame);

const styles = StyleSheet.create({
  noteLinkedAssayDataFrame: {display: 'flex'},
});
